/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ManufacturerDetails.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, IconButton, Stack, DialogContentText } from "@mui/material";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import deleteIcon from "../../assets/delete_icon.png";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { HandleApiActions } from "../../redux/actions/actionHandler";
import adminSettingsAPI from "../../redux/actions/adminSetttingAPI";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PropTypes from "prop-types";

const getManufacturersFromResponse = (resp) =>
  resp?.data ?? resp?.response?.data ?? resp?.data?.data ?? [];

const getTotalCountFromResponse = (resp, fallbackData) =>
  resp?.total_count ??
  resp?.totalItems ??
  resp?.total ??
  (Array.isArray(fallbackData) ? fallbackData.length : 0);

const mapManufacturers = ({
  manufacturersData,
  page,
  rowsPerPage,
  region,
  includeTimestamps,
}) =>
  manufacturersData?.map((item, index) => {
    const baseData = {
      ...item,
      id: item?.id || index,
      sno: (page - 1) * rowsPerPage + index + 1,
      mft_name: item?.name,
      mft_address: item?.address,
    };

    if (includeTimestamps && (item?.created_at || item?.updated_at)) {
      baseData.created_at = convertUtcToTimeZone(item?.created_at, region);
      baseData.updated_at = convertUtcToTimeZone(item?.updated_at, region);
    }

    return baseData;
  });

function ManufacturerDetails({
  open,
  handleClick,
  onExportDataReady,
  handleExportToExcel,
}) {
  const mftDetails = useSelector((state) => state.adminSettingsReducer);
  console.log("mftDetails", mftDetails);
  const timeZoneDetails = useSelector((state) => state.profileReducer);
  const dispatch = useDispatch();

  const [page, setPage] = React.useState(1);
  const [rowsPerPage] = React.useState(25);
  const [tableRowsData, setTableRowsData] = useState([]);
  const [openAddMftDetails, setOpenAddMftDetails] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [openDeleteMftDetails, setOpenDeleteMftDetails] = React.useState(false);
  const [openEditMftDetails, setOpenEditMftDetails] = React.useState(false);
  const [totalManufacturerCount, setTotalManufacturerCount] = useState(0);
  const searchQueryRef = useRef("");

  const handleTableRowClick = useCallback((params) => {
    const reqObj = {
      current_time: moment().format(),
      name: params?.row?.name,
      address: params?.row?.address,
    };
    setSelectedRow(reqObj);
    setOpenDeleteMftDetails(true);
  }, []);

  const COLUMNS = useMemo(() => {
    const firstItem =
      mftDetails?.adminSettings?.data?.vehicle_manufacturers?.[0];
    const hasTimestamps =
      firstItem && (firstItem.created_at || firstItem.updated_at);

    const baseColumns = [
      {
        field: "sno",
        headerName: "S.No",
        flex: 0.3,
        minWidth: 60,
      },
      {
        field: "mft_name",
        headerName: "Manufacturer Name",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "mft_address",
        headerName: "Address",
        flex: 1,
        minWidth: 150,
      },
    ];

    const timestampColumns = hasTimestamps
      ? [
          {
            field: "created_at",
            headerName: "Created",
            flex: 0.8,
            minWidth: 120,
          },
          {
            field: "updated_at",
            headerName: "Updated",
            flex: 0.8,
            minWidth: 120,
          },
        ]
      : [];

    const actionColumn = {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            sx={{ p: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              handleTableRowClick(params);
            }}
          >
            <img
              src={deleteIcon}
              alt="delete"
              style={{ width: 24, height: 24 }}
            />
          </IconButton>
        </Box>
      ),
    };

    return [...baseColumns, ...timestampColumns, actionColumn];
  }, [
    handleTableRowClick,
    mftDetails?.adminSettings?.data?.vehicle_manufacturers,
  ]);

  const supplierRef = useRef(null);
  useEffect(() => {
    if (open === "6") {
      const timeout = setTimeout(() => {
        if (
          supplierRef.current &&
          supplierRef.current.scrollHeight > supplierRef.current.clientHeight
        ) {
          supplierRef.current.scrollTop = supplierRef.current.scrollHeight;
        }
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  useEffect(() => {
    let isActive = true;
    (async () => {
      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_mft_details,
          show_toast: false,
          params: {
            page_number: page,
            page_size: rowsPerPage,
          },
        }),
      );

      if (!isActive) return;

      const manufacturersData = getManufacturersFromResponse(resp);
      const mappedData = mapManufacturers({
        manufacturersData,
        page,
        rowsPerPage,
        region: timeZoneDetails?.profile?.profile?.region,
        includeTimestamps: true,
      });

      setTableRowsData(mappedData);
      setTotalManufacturerCount(
        getTotalCountFromResponse(resp, manufacturersData),
      );
    })();

    return () => {
      isActive = false;
    };
  }, [dispatch, page, rowsPerPage, timeZoneDetails?.profile?.profile?.region]);

  const filteredRows = useMemo(() => tableRowsData, [tableRowsData]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  useEffect(() => {
    if (onExportDataReady && filteredRows) {
      const exportData = filteredRows.map((row) => ({
        "S.No": row.sno,
        "Manufacturer Name": row.mft_name,
        Address: row.mft_address,
        ...(row.created_at && { Created: row.created_at }),
        ...(row.updated_at && { Updated: row.updated_at }),
      }));
      onExportDataReady(exportData);
    }
  }, [filteredRows, onExportDataReady]);

  const toggleDialog = (newOpen) => {
    setOpenAddMftDetails(newOpen);
  };

  const handleAddMftDetails = () => {
    toggleDialog(true);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: {
            xs: "0 0px 2px 0px",
            md: "0 0px 4px 0px",
          },
          flexShrink: 0,
          mt: -2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box>
            <CustomSearchbarWithApiV2
              params={{
                page_number: page,
                page_size: rowsPerPage,
              }}
              apiAction={adminSettingsAPI.get_mft_details}
              searchKey={"name"}
              onSearchQueryChange={(query) => {
                if (query !== searchQueryRef.current) {
                  searchQueryRef.current = query;
                  setPage(1);
                }
              }}
              getDataOnSearch={(data, resp) => {
                const mappedData = mapManufacturers({
                  manufacturersData: data || [],
                  page,
                  rowsPerPage,
                  region: timeZoneDetails?.profile?.profile?.region,
                  includeTimestamps: true,
                });
                setTableRowsData(mappedData);
                setTotalManufacturerCount(
                  getTotalCountFromResponse(resp, data),
                );
              }}
              placeholder={"Search"}
            />
          </Box>
          <Box>
            <AddButtonV2 handleOnClick={handleAddMftDetails} />
          </Box>
        </Box>
        <Box>
          <CustomButtonV2
            startIcon={<FileDownloadOutlinedIcon />}
            type="submit"
            size="small"
            btnName="Export"
            handleOnClick={handleExportToExcel}
          />
        </Box>
      </Box>

      <Box
        sx={{
          height: "68vh", // ✅ Takes 70% of screen height
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CustomDataGridV2
          showPagination={true}
          rows={filteredRows}
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={totalManufacturerCount || filteredRows.length}
          handlePageChange={handlePageChange}
          columns={COLUMNS}
          isMultiLine={true}
        />
      </Box>

      <AddManufacturerDetails
        open={openAddMftDetails}
        toggleDialog={toggleDialog}
        setTableRowsData={setTableRowsData}
        setTotalManufacturerCount={setTotalManufacturerCount}
        rowsPerPage={rowsPerPage}
      />

      <EditManufacturerDetails
        open={openEditMftDetails}
        setOpenEditMftDetails={setOpenEditMftDetails}
        selectedRow={selectedRow}
        setTableRowsData={setTableRowsData}
        setTotalManufacturerCount={setTotalManufacturerCount}
        rowsPerPage={rowsPerPage}
        page={page}
      />

      <DeleteManufacturerDetails
        openDeleteMftDetails={openDeleteMftDetails}
        setOpenDeleteMftDetails={setOpenDeleteMftDetails}
        selectedRow={selectedRow}
        setPage={setPage}
        page={page}
        rowsPerPage={rowsPerPage}
        filteredRows={filteredRows}
        setTableRowsData={setTableRowsData}
        setTotalManufacturerCount={setTotalManufacturerCount}
      />
    </Box>
  );
}

export default ManufacturerDetails;

const AddManufacturerDetails = ({
  open,
  toggleDialog,
  setTableRowsData,
  setTotalManufacturerCount,
  rowsPerPage,
}) => {
  const dispatch = useDispatch();

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");

  const handleClose = () => {
    toggleDialog(false);

    setName("");
    setAddress("");
  };

  const handleClickAdd = () => {
    const reqObj = {
      current_time: moment().format(),
      name: name,
      address: address,
    };

    const addAndRefresh = async () => {
      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.add_mft_details,
          show_toast: true,
          params: reqObj,
        }),
      );

      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_mft_details,
          show_toast: false,
          params: {
            page_number: 1,
            page_size: rowsPerPage,
          },
        }),
      );

      const manufacturersData = getManufacturersFromResponse(resp);
      const mappedData = mapManufacturers({
        manufacturersData,
        page: 1,
        rowsPerPage,
        includeTimestamps: false,
      });

      setTableRowsData(mappedData || []);
      setTotalManufacturerCount(
        getTotalCountFromResponse(resp, manufacturersData),
      );
      toggleDialog(false);
      setName("");
      setAddress("");
    };

    addAndRefresh();
  };

  return (
    <div>
      <CustomDialogBoxV2
        open={open}
        onClose={handleClose}
        title={"Add Manufacturer"}
        successButtonName={"Add"}
        rejectButtonName={"Close"}
        onClickSuccess={handleClickAdd}
        onDisabled={
          !(
            name?.length > 2 &&
            name?.trim() !== "" &&
            address?.length > 2 &&
            address?.trim() !== ""
          )
        }
        actionButtonStyle={"continuous"}
        onClickReject={handleClose}
      >
        <Stack spacing={2} sx={{ width: "350px" }}>
          <CustomFormTextFieldV2
            label="Name"
            value={name}
            setValue={(val) => {
              if (val.length <= 35) {
                setName(val);
              }
            }}
            required={true}
            maxLength={35}
          />
          <CustomFormTextFieldV2
            label="Address"
            value={address}
            setValue={(val) => {
              if (val.length <= 35) {
                setAddress(val);
              }
            }}
            required={true}
            maxLength={35}
          />
        </Stack>
      </CustomDialogBoxV2>
    </div>
  );
};

const EditManufacturerDetails = ({
  open,
  setOpenEditMftDetails,
  selectedRow,
  setTableRowsData,
  setTotalManufacturerCount,
  rowsPerPage,
  page,
}) => {
  const dispatch = useDispatch();

  const [editName, setEditName] = useState("");
  const [editAddress, setEditAddress] = useState("");

  useEffect(() => {
    if (selectedRow) {
      setEditName(selectedRow?.name || selectedRow?.mft_name || "");
      setEditAddress(selectedRow?.address || selectedRow?.mft_address || "");
    }
  }, [selectedRow]);

  const handleClose = () => {
    setOpenEditMftDetails(false);
  };

  const handleClickEdit = async () => {
    const deleteReqObj = {
      current_time: moment().format(),
      name: selectedRow?.name,
      address: selectedRow?.address,
    };

    const addReqObj = {
      current_time: moment().format(),
      name: editName,
      address: editAddress,
    };

    await dispatch(
      HandleApiActions({
        ...adminSettingsAPI.delete_mft_details,
        show_toast: false,
        params: deleteReqObj,
      }),
    );

    await dispatch(
      HandleApiActions({
        ...adminSettingsAPI.add_mft_details,
        show_toast: true,
        params: addReqObj,
      }),
    );

    const resp = await dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_mft_details,
        show_toast: false,
        params: {
          page_number: page,
          page_size: rowsPerPage,
        },
      }),
    );

    const manufacturersData = getManufacturersFromResponse(resp);
    const mappedData = mapManufacturers({
      manufacturersData,
      page,
      rowsPerPage,
      includeTimestamps: false,
    });
    setTableRowsData(mappedData || []);
    setTotalManufacturerCount(
      getTotalCountFromResponse(resp, manufacturersData),
    );

    handleClose();
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title={"UPDATE MANUFACTURER DETAILS"}
      successButtonName={"Update"}
      rejectButtonName={"Close"}
      onClickSuccess={handleClickEdit}
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
    >
      <Stack spacing={2}>
        <CustomFormTextFieldV2
          label={"Name"}
          value={editName}
          setValue={setEditName}
          txtFldStyle={{
            width: "270px",
          }}
          required={true}
        />

        <CustomFormTextFieldV2
          label={"Address"}
          value={editAddress}
          setValue={setEditAddress}
          txtFldStyle={{
            width: "270px",
          }}
          required={true}
        />
      </Stack>
    </CustomDialogBoxV2>
  );
};

const DeleteManufacturerDetails = ({
  openDeleteMftDetails,
  setOpenDeleteMftDetails,
  selectedRow,
  setPage,
  page,
  rowsPerPage,
  filteredRows,
  setTableRowsData,
  setTotalManufacturerCount,
}) => {
  const dispatch = useDispatch();

  const refreshCurrentPage = async () => {
    const resp = await dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_mft_details,
        show_toast: false,
        params: {
          page_number: page,
          page_size: rowsPerPage,
        },
      }),
    );

    const manufacturersData = getManufacturersFromResponse(resp);
    const mappedData = mapManufacturers({
      manufacturersData,
      page,
      rowsPerPage,
      includeTimestamps: false,
    });

    setTableRowsData(mappedData || []);
    const totalCount = getTotalCountFromResponse(resp, manufacturersData);
    setTotalManufacturerCount(totalCount);
    if (Array.isArray(mappedData) && mappedData.length === 0 && page > 1) {
      setPage(page - 1);
    }
  };

  const handleDelete = () => {
    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.delete_mft_details,
        show_toast: true,
        params: selectedRow,
      }),
    ).then(() => {
      setTimeout(() => {
        refreshCurrentPage();
      }, 500);
    });
    setOpenDeleteMftDetails(false);
  };
  return (
    <CustomDialogBoxV2
      open={openDeleteMftDetails}
      onClose={() => setOpenDeleteMftDetails(false)}
      title={"Delete Manufacturer"}
      successButtonName={"Ok"}
      rejectButtonName={"Cancel"}
      onClickSuccess={handleDelete}
      onClickReject={() => setOpenDeleteMftDetails(false)}
      actionButtonStyle={"continuous"}
    >
      <Box
        sx={{
          width: "350px",
          height: "50px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <DialogContentText>Are you sure you want to delete?</DialogContentText>
      </Box>
    </CustomDialogBoxV2>
  );
};

const selectedManufacturerShape = PropTypes.shape({
  name: PropTypes.string,
  address: PropTypes.string,
  mft_name: PropTypes.string,
  mft_address: PropTypes.string,
});

ManufacturerDetails.propTypes = {
  open: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleClick: PropTypes.func,
  onExportDataReady: PropTypes.func,
  handleExportToExcel: PropTypes.func,
};

AddManufacturerDetails.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func,
  setTableRowsData: PropTypes.func,
  setTotalManufacturerCount: PropTypes.func,
  rowsPerPage: PropTypes.number,
};

EditManufacturerDetails.propTypes = {
  open: PropTypes.bool,
  setOpenEditMftDetails: PropTypes.func,
  selectedRow: selectedManufacturerShape,
  setTableRowsData: PropTypes.func,
  setTotalManufacturerCount: PropTypes.func,
  rowsPerPage: PropTypes.number,
  page: PropTypes.number,
};

DeleteManufacturerDetails.propTypes = {
  openDeleteMftDetails: PropTypes.bool,
  setOpenDeleteMftDetails: PropTypes.func,
  selectedRow: selectedManufacturerShape,
  setPage: PropTypes.func,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  filteredRows: PropTypes.array,
  setTableRowsData: PropTypes.func,
  setTotalManufacturerCount: PropTypes.func,
};
